import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, CircleMarker, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import { getDives } from '../api';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet marker icons in React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl:       'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl:     'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

/** Zoom naar een specifieke locatie als lat/lon meegegeven zijn via URL */
function FlyTo({ lat, lon }) {
  const map = useMap();
  useEffect(() => {
    if (lat && lon) {
      map.flyTo([lat, lon], 14, { duration: 1.2 });
    }
  }, [lat, lon, map]);
  return null;
}

/** Reageert op slider-wijzigingen zonder de kaart te hermonteren */
function ZoomController({ zoom }) {
  const map = useMap();
  useEffect(() => { map.setZoom(zoom); }, [zoom]);
  return null;
}

const ZOOM_KM = { 4:'2000 km', 5:'1000 km', 6:'500 km', 7:'200 km', 8:'100 km', 9:'75 km', 10:'50 km', 11:'25 km', 12:'10 km', 13:'5 km', 14:'2 km' };

const LAYERS = {
  street: {
    label: '🗺️ Kaart',
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '© <a href="https://www.openstreetmap.org/">OpenStreetMap</a>',
  },
  satellite: {
    label: '🛰️ Satelliet',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: '© <a href="https://www.esri.com/">Esri</a>',
  },
};

export default function MapPage() {
  const [dives, setDives]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [layer, setLayer]       = useState('satellite');
  const [searchParams]          = useSearchParams();
  const [mapZoom, setMapZoom]   = useState(10); // ~50 km standaard
  const navigate = useNavigate();

  const focusLat = parseFloat(searchParams.get('lat'));
  const focusLon = parseFloat(searchParams.get('lon'));
  const focusId  = parseInt(searchParams.get('id'));

  useEffect(() => {
    getDives().then(setDives).finally(() => setLoading(false));
  }, []);

  const mapped = dives.filter(d => d.latitude && d.longitude);
  const noGps  = dives.length - mapped.length;

  if (loading) return <div className="loading">Kaart laden…</div>;

  // Startcentrum: focuspunt via URL, anders eerste duik, anders België
  const center = focusLat && focusLon
    ? [focusLat, focusLon]
    : mapped.length > 0
      ? [mapped[0].latitude, mapped[0].longitude]
      : [50.85, 4.35];

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 className="page-title">Kaart</h1>
          <p className="page-subtitle">
            {mapped.length} duiken op kaart
            {noGps > 0 && ` · ${noGps} zonder GPS`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {Object.entries(LAYERS).map(([key, l]) => (
            <button
              key={key}
              onClick={() => setLayer(key)}
              style={{
                padding: '7px 14px', borderRadius: 8, border: '1px solid var(--border)',
                fontSize: 13, cursor: 'pointer',
                background: layer === key ? 'var(--blue)' : 'var(--card)',
                color: layer === key ? '#fff' : 'var(--text)',
                fontWeight: layer === key ? 600 : 400,
              }}
            >
              {l.label}
            </button>
          ))}
        </div>
      </div>

      {mapped.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 48 }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🗺️</div>
          <p style={{ color: 'var(--text-muted)' }}>
            Nog geen GPS-coördinaten beschikbaar.<br />
            Importeer je Oceanic+ data om duiklocaties op de kaart te zien.
          </p>
        </div>
      ) : (
        <>
        <div className="card" style={{ padding: 0, overflow: 'hidden', height: 600 }}>
          <MapContainer
            center={center}
            zoom={mapZoom}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              key={layer}
              attribution={LAYERS[layer].attribution}
              url={LAYERS[layer].url}
            />

            {/* Vlieg naar focuspunt indien meegegeven via URL */}
            {focusLat && focusLon && <FlyTo lat={focusLat} lon={focusLon} />}
            <ZoomController zoom={mapZoom} />

            {mapped.map(d => {
              const isFocus  = d.id === focusId;
              const hasExit  = d.latitude_exit && d.longitude_exit;
              const popup = (
                <Popup autoPan={false}>
                  <div>
                    <strong>{d.location_name || d.site_name || 'Locatie onbekend'}</strong><br />
                    {d.dive_date} · {d.dive_number ? `#${d.dive_number}` : ''}<br />
                    Max: {d.max_depth_m}m<br />
                    <button
                      onClick={() => navigate(`/dives/${d.id}`)}
                      style={{ marginTop: 6, padding: '4px 10px', background: 'var(--blue)', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}
                    >
                      Details bekijken
                    </button>
                  </div>
                </Popup>
              );

              return (
                <React.Fragment key={d.id}>
                  {/* Instappunt */}
                  {isFocus ? (
                    <Marker position={[d.latitude, d.longitude]}>{popup}</Marker>
                  ) : (
                    <CircleMarker
                      center={[d.latitude, d.longitude]}
                      radius={6}
                      pathOptions={{ color: '#fff', weight: 1.5, fillColor: '#0066ff', fillOpacity: 0.8 }}
                    >{popup}</CircleMarker>
                  )}

                  {/* Rode lijn + uitstappunt */}
                  {hasExit && (
                    <>
                      <Polyline
                        positions={[[d.latitude, d.longitude], [d.latitude_exit, d.longitude_exit]]}
                        pathOptions={{ color: '#ef4444', weight: 2, dashArray: '5 4', opacity: 0.85 }}
                      />
                      <CircleMarker
                        center={[d.latitude_exit, d.longitude_exit]}
                        radius={5}
                        pathOptions={{ color: '#fff', weight: 1.5, fillColor: '#ef4444', fillOpacity: 0.9 }}
                      >
                        <Popup autoPan={false}>
                          <div>
                            <strong>🚪 Uitstap · {d.location_name || d.site_name || ''}</strong><br />
                            {d.dive_date}<br />
                            <button onClick={() => navigate(`/dives/${d.id}`)}
                              style={{ marginTop: 6, padding: '4px 10px', background: 'var(--blue)', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>
                              Details bekijken
                            </button>
                          </div>
                        </Popup>
                      </CircleMarker>
                    </>
                  )}
                </React.Fragment>
              );
            })}
          </MapContainer>
        </div>

        {/* Zoom slider */}
        <div className="card" style={{ marginTop: 8, padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 13, flexShrink: 0 }}>🔍</span>
          <input
            type="range" min={4} max={14} step={1} value={mapZoom}
            onChange={e => setMapZoom(Number(e.target.value))}
            style={{ flex: 1, accentColor: 'var(--blue)', cursor: 'pointer' }}
          />
          <span style={{ fontSize: 11, color: 'var(--text-muted)', minWidth: 52, textAlign: 'right', flexShrink: 0 }}>
            ~{ZOOM_KM[mapZoom]}
          </span>
        </div>
        </>
      )}
    </div>
  );
}
